import {
	Button,
	ChipConfirmModal,
	ChipLink,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useAccess } from "~/core/access/index";
import { ApiError, api, type PageSummary } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Async, Empty } from "~/core/ui/states";
import { Drill, DrillLink } from "~/modules/analytics/components/detail";
import { GenerationProgress } from "~/modules/analytics/components/generation-progress";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { matches, TableTools } from "~/modules/analytics/components/table";
import { trackedGenerations, trackGeneration } from "~/modules/analytics/lib/generation-tracking";
import { readingPath } from "~/modules/analytics/lib/loop";

const SITE = import.meta.env.VITE_SITE_ORIGIN || "http://localhost:9901";

/** Which half of the loop this screen is: what is waiting, or what is live. */
type Props = ReadingProps & { stage: "draft" | "published" };

/**
 * Generated pages, split by what the customer is asked to do with them.
 *
 * One list of everything made "approve this" and "this is live" look like the
 * same state, and they are the two steps of the loop a person acts on
 * differently: a draft is a decision, a published page is a measurement.
 */
export function PagesPage({ entityId, filters, setFilters, stage }: Props) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const [busy, setBusy] = useState<string | null>(null);
	const [query, setQuery] = useState("");
	const [dismissed, setDismissed] = useState<Set<string>>(new Set());
	// The page waiting on a confirmation, and the only reason this list needs
	// state of its own. One dialog for the table rather than one mounted behind
	// every published row.
	const [taking, setTaking] = useState<PageSummary | null>(null);
	const drafts = stage === "draft";

	const queryClient = useQueryClient();
	const state = useQuery({
		queryKey: [
			...keys.entity(entityId),
			"stage",
			drafts,
			{ range: filters.range, engine: filters.engine, locale: filters.locale },
		],
		queryFn: async () => {
			const jobs = drafts ? await api.generationJobs(entityId, filters) : [];
			for (const job of jobs) {
				if (job.status === "queued" || job.status === "running") trackGeneration(job.id);
			}
			return { jobs, pages: await api.pages(entityId, filters) };
		},
		...read,
		// Drafts are watched: the pages being written land here while it is open.
		refetchInterval: drafts ? 5000 : false,
	});
	// Approving and taking down are one write each, and both change a row this
	// list shows.
	const change = useMutation({
		mutationFn: (run: () => Promise<unknown>) => run(),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});

	async function act(pageId: string, run: () => Promise<unknown>) {
		setBusy(pageId);
		try {
			await change.mutateAsync(run);
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(null);
		}
	}

	return (
		<StageShell
			entityId={entityId}
			reading={drafts ? "drafts" : "published"}
			lede={t(drafts ? "pages.draftsLede" : "pages.publishedLede")}
			filters={filters}
			setFilters={setFilters}
			actions={
				// The one filled control on this screen. Asking for a page is an
				// action on this list rather than a place beside it, so it is a
				// button here and not a row in the navigation.
				//
				// Gone rather than greyed for a viewer: it is a link, and a link
				// that cannot be disabled would open a composer whose only
				// button is dead.
				drafts && editor ? (
					<ChipLink variant="primary" href={`/w/${entityId}/compose`}>
						{t("compose.title")}
					</ChipLink>
				) : undefined
			}
		>
			<Async state={state}>
				{({ pages: all, jobs }) => {
					const tracked = trackedGenerations();
					const current = jobs.filter((job) => tracked.has(job.id) && !dismissed.has(job.id));
					const staged = all.filter((page) => inStage(page, stage));
					if (staged.length === 0 && current.length === 0) return <EmptyStage entityId={entityId} drafts={drafts} />;

					// Searched on what is printed in the first column: the title, and
					// the kind and slug set under it. A published list gets long, and
					// the question asked of a long one is "where is that page".
					const rows = staged.filter((page) => matches(query, page.title, page.kind, page.slug));

					return (
						<>
							<GenerationProgress
								entityId={entityId}
								jobs={current}
								pages={all}
								onDismiss={(id) => {
									trackGeneration(id, false);
									setDismissed((previous) => new Set([...previous, id]));
								}}
							/>
							{staged.length > 0 ? (
								<>
									<TableTools
										query={query}
										onQuery={setQuery}
										placeholder={t("table.title")}
										shown={rows.length}
										total={staged.length}
										csv={{ name: drafts ? "drafts" : "published", rows: csv(rows) }}
									/>

									{rows.length === 0 ? (
										<p className="empty">{t("table.noMatch")}</p>
									) : (
										<div className="table-scroll">
											<Table
												variant="resource"
												className="record-table max-[720px]:[&_td]:p-0 max-[720px]:[&_td]:whitespace-normal max-[720px]:[&_td.num]:text-left"
											>
												<TableHeader>
													<TableRow>
														<TableHead className="wrap">{t("table.title")}</TableHead>
														<TableHead>{t("table.status")}</TableHead>
														{drafts ? null : <TableHead className="num">{t("pages.crawled")}</TableHead>}
														{drafts ? null : <TableHead className="num">{t("pages.cited")}</TableHead>}
														<TableHead>{t(drafts ? "table.updated" : "pages.publishedAt")}</TableHead>
														<TableHead />
													</TableRow>
												</TableHeader>
												<TableBody>
													{rows.map((page) => (
														<TableRow key={page.id}>
															<TableCell className="wrap">
																{page.title}
																{/* What this page is for. A draft with no gap behind it
														    was asked for by a person in the composer, and saying
														    so is the difference between "why does this exist"
														    and "somebody asked for it". */}
																{page.gap_id ? (
																	<div className="faint">
																		<DrillLink to={readingPath(`/w/${entityId}`, "gaps")}>
																			{t("pages.closesGap", { question: page.gap_question ?? "" })}
																		</DrillLink>
																	</div>
																) : null}
																<div className="faint">
																	{page.kind} · {page.locale} · {t("pages.revision", { n: page.revision })}
																</div>
															</TableCell>
															<TableCell data-label={t("table.status")}>
																<span className={statusClass(page.status)}>{t(`pages.status.${page.status}`)}</span>
																{page.has_live_version && page.status !== "published" ? (
																	<div className="faint">{t("pages.previousLive")}</div>
																) : null}
															</TableCell>
															{drafts ? null : (
																// The step before the citation. A page that is live and
																// has never been fetched is a different problem from a
																// page that is fetched and never cited, and the two used
																// to look identical from here.
																<TableCell
																	data-label={t("pages.crawled")}
																	className={page.crawled === 0 ? "num mono warn" : "num mono"}
																>
																	{page.crawled ? (
																		<Drill
																			view={{
																				kind: "crawler",
																				label: page.title,
																				path: pagePath(page.kind, page.slug, page.locale),
																			}}
																		>
																			{page.crawled}
																		</Drill>
																	) : (
																		"—"
																	)}
																</TableCell>
															)}
															{drafts ? null : (
																<TableCell className="num mono" data-label={t("pages.cited")}>
																	{page.cited ? (
																		// No range of its own: the count is taken over the
																		// window in the bar above, and a drawer opened at a
																		// different one would list a different number of
																		// rows from the figure that opened it.
																		<Drill
																			view={{
																				kind: "citations",
																				label: page.title,
																				filters: { page: page.id },
																			}}
																		>
																			{page.cited}
																		</Drill>
																	) : (
																		"—"
																	)}
																</TableCell>
															)}
															<TableCell
																className="mono"
																data-label={t(drafts ? "table.updated" : "pages.publishedAt")}
															>
																{fmt.relative(
																	locale,
																	drafts ? page.updated_at : (page.published_at ?? page.updated_at),
																)}
															</TableCell>
															<TableCell className="num">
																<div className="record-actions">
																	{/* Reading the draft is the primary action here, not
													    approving one nobody has opened. */}
																	<Button asChild variant="default">
																		<Link to={`/w/${entityId}/pages/${page.id}`}>{t("editor.title")}</Link>
																	</Button>
																	{page.status === "review" || page.has_live_version ? (
																		<DropdownMenu>
																			<DropdownMenuTrigger asChild>
																				<Button variant="default" size="icon" aria-label={t("table.moreActions")}>
																					<MoreHorizontal />
																				</Button>
																			</DropdownMenuTrigger>
																			<DropdownMenuContent align="end">
																				{page.status === "review" ? (
																					<DropdownMenuItem
																						disabled={busy === page.id || !editor}
																						onSelect={() =>
																							act(page.id, async () => {
																								const result = await api.approvePage(page.id, page.revision);
																								if (result.status !== "published")
																									toast.info(t("pages.publishPending"));
																							})
																						}
																					>
																						{t("pages.approve")}
																					</DropdownMenuItem>
																				) : null}
																				{page.has_live_version ? (
																					<>
																						<DropdownMenuItem asChild>
																							<a
																								href={pageUrl(page.kind, page.slug, page.locale)}
																								target="_blank"
																								rel="noopener"
																							>
																								{t("pages.view")}
																							</a>
																						</DropdownMenuItem>
																						<DropdownMenuItem
																							disabled={busy === page.id || !editor}
																							onSelect={() => setTaking(page)}
																						>
																							{t("pages.unpublish")}
																						</DropdownMenuItem>
																					</>
																				) : null}
																			</DropdownMenuContent>
																		</DropdownMenu>
																	) : null}
																</div>
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</div>
									)}
								</>
							) : current.length === 0 ? (
								<EmptyStage entityId={entityId} drafts={drafts} />
							) : null}
						</>
					);
				}}
			</Async>

			{drafts ? (
				<Button asChild variant="link">
					<Link to={`/w/${entityId}/tasks?kind=generate.page`}>{t("pages.generationHistory")}</Link>
				</Button>
			) : null}

			{/* Outside the async block, so a reload underneath it cannot take the
			    confirmation off the screen while it is being read. */}
			<TakeDown
				page={taking}
				onClose={() => setTaking(null)}
				onConfirm={(page) => act(page.id, () => api.unpublishPage(page.id))}
			/>
		</StageShell>
	);
}

/**
 * Taking a page down asks first, and says what it costs.
 *
 * Not because the click is hard to undo in this database — it is one flag —
 * but because the URL is the asset. An answer engine that has learned a page
 * and then meets a 410 does not come back on the next crawl.
 */
function TakeDown({
	page,
	onClose,
	onConfirm,
}: {
	/** The page being taken down, or null when nothing is. */
	page: PageSummary | null;
	onClose: () => void;
	onConfirm: (page: PageSummary) => void;
}) {
	const t = useT();
	return (
		<ChipConfirmModal
			open={page !== null}
			onOpenChange={(open) => (open ? null : onClose())}
			title={t("pages.unpublishTitle")}
			text={t("pages.unpublishBody")}
			dismissLabel={t("common.cancel")}
			confirm={{
				label: t("pages.unpublish"),
				variant: "destructive",
				onClick: () => {
					if (page) onConfirm(page);
					onClose();
				},
			}}
		/>
	);
}

function EmptyStage({ entityId, drafts }: { entityId: string; drafts: boolean }) {
	const t = useT();
	return (
		<Empty
			title={t(drafts ? "pages.draftsEmpty" : "pages.publishedEmpty")}
			action={
				// Quiet: the filled one is in the header, and two filled buttons on
				// a screen means the screen has no primary action.
				drafts ? (
					<Button asChild variant="default">
						<Link to={readingPath(`/w/${entityId}`, "gaps")}>{t("pages.toGaps")}</Link>
					</Button>
				) : undefined
			}
		/>
	);
}

/** Draft and review are work in progress; published and unpublished are history. */
function inStage(page: PageSummary, stage: "draft" | "published"): boolean {
	const waiting = page.status === "draft" || page.status === "review";
	return stage === "draft" ? waiting : page.has_live_version || !waiting;
}

function statusClass(status: string): string {
	switch (status) {
		case "published":
			return "pill pill-success";
		case "review":
			return "pill pill-warning";
		case "unpublished":
			return "pill pill-danger";
		default:
			return "pill";
	}
}

/**
 * Mirrors the server's `canonical_path`.
 *
 * One function here as there, because the crawler counts match a fetched path
 * back to a page by string: a route added on one side and not the other is a
 * page whose traffic silently belongs to nobody.
 */
function pagePath(kind: string, slug: string, locale: string): string {
	const prefix = locale === "en" ? "" : `/${locale}`;
	const path =
		kind === "entity"
			? `/w/${slug}`
			: kind === "alternatives"
				? `/w/${slug}/alternatives`
				: kind === "dataset"
					? `/data/${slug}`
					: kind === "report"
						? `/reports/${slug}`
						: `/${kind}/${slug}`;
	return `${prefix}${path}`;
}

function pageUrl(kind: string, slug: string, locale: string): string {
	return `${SITE}${pagePath(kind, slug, locale)}`;
}

function csv(rows: PageSummary[]): (string | number)[][] {
	return [
		["title", "kind", "locale", "status", "revision", "url", "updated_at", "published_at"],
		...rows.map((page) => [
			page.title,
			page.kind,
			page.locale,
			page.status,
			page.revision,
			pageUrl(page.kind, page.slug, page.locale),
			page.updated_at,
			page.published_at ?? "",
		]),
	];
}
